import { runPipeline, type ColorMode, type Quad, type RasterImage } from './imageProcessing';

// Dispatches heavy document processing to a Web Worker (keeping the UI thread responsive), with a
// main-thread fallback when workers are unavailable. terminate() releases the worker to avoid leaks.

let worker: Worker | null = null;
let seq = 0;
const pending = new Map<number, { resolve: (v: RasterImage) => void; reject: (e: Error) => void }>();

function ensureWorker(): Worker | null {
  if (typeof Worker === 'undefined') return null;
  if (!worker) {
    try {
      worker = new Worker(new URL('./imageProcessing.worker.ts', import.meta.url), { type: 'module' });
      worker.onmessage = (event: MessageEvent<{ id: number; result?: RasterImage; error?: string }>) => {
        const entry = pending.get(event.data.id);
        if (!entry) return;
        pending.delete(event.data.id);
        if (event.data.error || !event.data.result) entry.reject(new Error(event.data.error ?? 'processing failed'));
        else entry.resolve(event.data.result);
      };
      worker.onerror = () => { worker?.terminate(); worker = null; };
    } catch { worker = null; }
  }
  return worker;
}

export function processDocumentImage(image: RasterImage, mode: ColorMode = 'color', quad?: Quad): Promise<RasterImage> {
  const w = ensureWorker();
  if (!w) return Promise.resolve(runPipeline(image, mode, quad)); // fallback keeps behaviour identical
  return new Promise((resolve, reject) => {
    const id = seq += 1;
    pending.set(id, { resolve, reject });
    try { w.postMessage({ id, image, mode, quad }, [image.data.buffer]); }
    catch { pending.delete(id); resolve(runPipeline(image, mode, quad)); }
  });
}

export function terminateProcessor(): void {
  worker?.terminate();
  worker = null;
  pending.clear();
}
